// One Idempotency-Key per user action. A retry of the same action (same name, same payload)
// reuses its key, so the server replays the original response instead of creating a duplicate.
// A changed payload starts a new key, because the server answers the same key with a different
// body with 422. A finished action resets its key, so the next action is a new operation.
import {useState} from 'react';
import {newIdempotencyKey} from '../api/client';

/** cyrb53: a short non-cryptographic hash, so passwords and tokens are not kept in memory. */
const hash = (str) => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i += 1) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return String(4294967296 * (2097151 & h2) + (h1 >>> 0));
};

/** Key-order-independent serialization of a request payload (files are described, not read). */
export const canonical = (value) => {
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return `blob:${value.name || ''}:${value.size}:${value.lastModified || 0}`;
  }
  if (value === null || typeof value !== 'object') return String(JSON.stringify(value));
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
};

/**
 * The key store behind `useIdempotencyKeys`. Exported separately so the rules can be tested
 * without a renderer. `makeKey` is injectable for tests.
 */
export const createIdempotencyKeys = (makeKey = newIdempotencyKey) => {
  const slots = new Map();

  /** Same name and payload as the last call -> the same key (a retry); otherwise a new key. */
  const keyFor = (name, payload) => {
    const fingerprint = hash(canonical(payload));
    const slot = slots.get(name);
    if (slot && slot.fingerprint === fingerprint) return slot.key;
    const key = makeKey();
    slots.set(name, {fingerprint, key});
    return key;
  };

  /** The action is over (succeeded, or its result is final): the next attempt gets a new key. */
  const reset = (name) => {
    slots.delete(name);
  };

  /**
   * A failed attempt. A 422 means the server refused this key for this payload, so the key is
   * dropped and the next attempt starts clean. Any other failure keeps the key, so a retry
   * replays the original request. Accepts axios errors (`err.response.status`) and the auth
   * error classes, which carry `status`.
   */
  const settle = (name, err) => {
    const status = (err && ((err.response && err.response.status) || err.status)) || null;
    if (status === 422) slots.delete(name);
  };

  /**
   * Runs one user action: `send(key)` with the key for this name and payload. Resets the key
   * when the action resolves, and settles it when the action rejects.
   */
  const run = (name, payload, send) => {
    const key = keyFor(name, payload);
    return Promise.resolve()
      .then(() => send(key))
      .then(
        (result) => {
          reset(name);
          return result;
        },
        (err) => {
          settle(name, err);
          throw err;
        },
      );
  };

  return {keyFor, reset, settle, run};
};

/** Per-component key store: stable across renders, one slot per named action. */
export default function useIdempotencyKeys() {
  const [keys] = useState(() => createIdempotencyKeys());
  return keys;
}
